/**
 * CommunityDeclarationFields — DEV-1413 (slice 2).
 *
 * The reusable primary (home) + operating community declaration step.
 * Renders two labeled <GeoLocationPicker> fields over the full geography
 * tree (inactive geos included — declaration ≠ community page, §8.4
 * ruling 2). Primary is required; operating is optional and defaults to
 * primary (§8.11 ruling 1) — the caller applies the default at submit
 * time (or lets the register endpoint default it server-side).
 *
 * Consumers today: the signup form (`LandingSignupForm`, pre-auth data
 * via `declarationTree.ts`). Built label-agnostic (all user-facing
 * strings arrive via props) so slice 3's email-invite onboarding wizard
 * can reuse it with its own namespace, and authenticated surfaces can
 * feed it the full entities/relationships arrays instead of the
 * pre-auth payload.
 *
 * Tokens-only colors and logical properties throughout (RTL safe).
 */

import React from 'react';
import GeoLocationPicker from '../GeoLocationPicker/GeoLocationPicker';
import styles from './CommunityDeclarationFields.module.css';

export interface CommunityDeclarationFieldsProps {
  /** Community entities in snake_case shape (entity_guid / entity_type / name). */
  entities: any[];
  /** Parent/child relationships in snake_case shape. */
  relationships: any[];
  /** Selected primary (home) community GUID, '' when unset. REQUIRED field. */
  primaryValue: string;
  onPrimaryChange: (guid: string) => void;
  /** Selected operating community GUID, '' when unset (defaults to primary). */
  operatingValue: string;
  onOperatingChange: (guid: string) => void;
  /** Localized label for the primary field (caller owns i18n). */
  primaryLabel: string;
  /** Optional help text rendered under the primary field. */
  primaryHint?: string;
  /** Localized label for the operating field. */
  operatingLabel: string;
  /** Localized hint rendered under the operating field. */
  operatingHint?: string;
  /** Localized typeahead placeholder, shared by both pickers. */
  placeholder?: string;
  disabled?: boolean;
  /** Show the operating-community field. Default true. Set false to collect
      only the home community (operating defaults to home server-side, §8.11
      r1) — used by the invitation accept form to reduce signup friction. */
  showOperating?: boolean;
}

const CommunityDeclarationFields: React.FC<CommunityDeclarationFieldsProps> = ({
  entities,
  relationships,
  primaryValue,
  onPrimaryChange,
  operatingValue,
  onOperatingChange,
  primaryLabel,
  operatingLabel,
  operatingHint,
  primaryHint,
  placeholder,
  disabled = false,
  showOperating = true,
}) => {
  return (
    <div className={styles.root}>
      <div className={styles.fieldGroup}>
        <label className={styles.label}>
          {primaryLabel}
          <span className={styles.required} aria-hidden="true"> *</span>
        </label>
        <GeoLocationPicker
          value={primaryValue}
          onChange={onPrimaryChange}
          entities={entities}
          relationships={relationships}
          placeholder={placeholder}
          disabled={disabled}
        />
        {primaryHint && <div className={styles.hint}>{primaryHint}</div>}
      </div>

      {showOperating && (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{operatingLabel}</label>
          <GeoLocationPicker
            value={operatingValue}
            onChange={onOperatingChange}
            entities={entities}
            relationships={relationships}
            placeholder={placeholder}
            disabled={disabled}
          />
          {operatingHint && <div className={styles.hint}>{operatingHint}</div>}
        </div>
      )}
    </div>
  );
};

export default CommunityDeclarationFields;
